// Pure grouping for the review workspace (Access Reviews 1.1): by person, by role, and what "Keep all" may touch.
import { NO_BULK_ROLE_IDS } from '../../../../shared/access-reviews.mjs';

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const cmp = (a, b) => collator.compare(a ?? '', b ?? '');
const holderName = (i) => i.holder?.displayName ?? '';
const holderKey = (i) => i.holderId ?? `${holderName(i)}|${i.holder?.upn ?? ''}`;

/** One group per person, sorted by display name; each person's items by role, then scope. */
export function groupByPerson(items) {
  const map = new Map();
  for (const i of items ?? []) {
    const k = holderKey(i);
    if (!map.has(k)) map.set(k, { holderId: i.holderId ?? k, holder: i.holder, items: [] });
    map.get(k).items.push(i);
  }
  return [...map.values()]
    .map((g) => ({ ...g, items: g.items.sort((a, b) => cmp(a.role, b.role) || cmp(a.scopeLabel, b.scopeLabel)) }))
    .sort((a, b) => cmp(a.holder?.displayName, b.holder?.displayName));
}

/** One group per role, sorted by role name; each role's items by person, then scope. */
export function groupByRole(items) {
  const map = new Map();
  for (const i of items ?? []) {
    const k = i.roleTemplateId ?? i.role;
    if (!map.has(k)) map.set(k, { roleTemplateId: i.roleTemplateId ?? k, role: i.role, roleHelp: i.roleHelp ?? null, items: [] });
    map.get(k).items.push(i);
  }
  return [...map.values()]
    .map((g) => ({ ...g, items: g.items.sort((a, b) => cmp(holderName(a), holderName(b)) || cmp(a.scopeLabel, b.scopeLabel)) }))
    .sort((a, b) => cmp(a.role, b.role));
}

/**
 * What "Keep all" may decide in one go: undecided items that are not Global Administrator or Privileged Role
 * Administrator, not flagged, and not the viewer's own access (`ownAccess` true) or access that might include it
 * (`ownAccess` 'unchecked': a group whose members are not fully known). The server computes `ownAccess` from the
 * directory, the same way it checks a decision, so this never disagrees with what it accepts. `skipped` counts the
 * undecided items left out, which need one decision each.
 */
export function keepAllEligible(items) {
  const eligible = [];
  let skipped = 0;
  for (const i of items ?? []) {
    if (i.decision) continue;
    const own = i.ownAccess === true || i.ownAccess === 'unchecked';
    const barred = i.bulkEligible === false || i.needsText === true || NO_BULK_ROLE_IDS.includes(i.roleTemplateId);
    if (own || barred) skipped += 1; else eligible.push(i);
  }
  return { eligible, skipped };
}

/** True for the admin workspace payload of GET mine: only it carries the assigned reviewer on every item. */
export const isWorkspaceView = (view) => view?.state === 'ready' && Array.isArray(view.items) && view.items.some((i) => i.reviewer !== undefined);

/** True when the items can be grouped (the holder id is on every payload of the server, admin or reviewer). */
export const isGroupable = (items) => Array.isArray(items) && items.some((i) => i.holderId !== undefined);
